/*
 *   File : about.js
 *   Author : https://evoqins.com
 *   Description : about component
 *   Integrations : NA
 *   Version : 1.0.0
 */
import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { CustomFileUpload, Input } from "../../../Components/FormElements";
import { PrimaryButton } from "../../../Components/Buttons";

/**
 * AboutSections (controlled)
 *
 * Props:
 *  - data: Array of about items: [{ id, text, file, fileUrl }]
 *  - onChange: function(updatedArray) -> called whenever items change
 *  - maxItems: optional maximum number of about entries (default 5)
 *  - errorState: { general?: string, items?: [{ textError?: string, fileError?: string }] }
 *  - setErrorState: function(nextErrorState) -> parent state setter (optional)
 */
export default function AboutSections({
  data,
  onChange,
  maxItems = 5,
  errorState = {},
  setErrorState,
}) {
  // Helper to create a fresh about item
  const makeEmptyItem = () => ({
    id: `about_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
    text: "",
    file: null,
    fileUrl: "",
  });

  // If parent provided no data, initialize with one empty item (once on mount)
  useEffect(() => {
    if (!Array.isArray(data) || data.length === 0) {
      onChange && onChange([makeEmptyItem()]);
    }
  }, []);

  // helper to safely read item-level errors
  const getItemErrors = (idx) => {
    if (!errorState || !Array.isArray(errorState.items)) return {};
    return errorState.items[idx] || {};
  };

  // helper to clear specific error fields for an item and clear general error
  const clearItemErrors = (idx, fields = []) => {
    if (typeof setErrorState !== "function") return;
    setErrorState((prev = {}) => {
      const next = { ...prev };
      // clear general error
      const items = Array.isArray(next.items) ? [...next.items] : [];
      const prevItem = items[idx] ? { ...items[idx] } : {};
      fields.forEach((f) => {
        if (prevItem[f]) prevItem[f] = "";
      });
      items[idx] = prevItem;
      next.items = items;
      return next;
    });
  };

  // ---------- internal helper operations that notify parent ----------
  const updateItemAtIndex = (index, patch) => {
    const current = Array.isArray(data) ? [...data] : [];
    if (index < 0 || index >= current.length) return;
    current[index] = { ...current[index], ...patch };
    onChange && onChange(current);
  };

  const handleTextChange = (index, value) => {
    updateItemAtIndex(index, { text: value });
    // clear text error for this item
    clearItemErrors(index, ["textError"]);
  };

  const handleFileSelect = (index, file) => {
    updateItemAtIndex(index, { file });
    // clear file error for this item
    clearItemErrors(index, ["fileError"]);
  };

  const handleFileUploadComplete = (index, url) => {
    updateItemAtIndex(index, { fileUrl: url });
    // once uploaded, clear file error
    clearItemErrors(index, ["fileError"]);
  };

  const handleFileClear = (index) => {
    updateItemAtIndex(index, { file: null, fileUrl: "" });
    // possibly set a file error? here we clear any previous fileError (parent may revalidate)
    clearItemErrors(index, ["fileError"]);
  };

  const addAboutItem = () => {
    const current = Array.isArray(data) ? [...data] : [];
    if (current.length >= maxItems) return;
    current.push(makeEmptyItem());
    onChange && onChange(current);

    // keep errorState items in sync by adding an empty error object for new item
    if (typeof setErrorState === "function") {
      setErrorState((prev = {}) => {
        const next = { ...prev };
        const items = Array.isArray(next.items) ? [...next.items] : [];
        items.push({}); // empty errors for new item
        next.items = items;
        return next;
      });
    }
  };

  const removeAboutItem = (index) => {
    const current = Array.isArray(data) ? [...data] : [];
    if (current.length <= 1) {
      // keep at least one item: clear the only item
      current[0] = { ...current[0], text: "", file: null, fileUrl: "" };
      onChange && onChange(current);
      // clear errors for the only item
      if (typeof setErrorState === "function") {
        setErrorState((prev = {}) => {
          const next = { ...prev };
          next.items =
            Array.isArray(next.items) && next.items[0]
              ? [{ textError: "", fileError: "" }]
              : [];
          return next;
        });
      }
      return;
    }
    current.splice(index, 1);
    onChange && onChange(current);

    // remove corresponding errors entry
    if (typeof setErrorState === "function") {
      setErrorState((prev = {}) => {
        const next = { ...prev };
        const items = Array.isArray(next.items) ? [...next.items] : [];
        items.splice(index, 1);
        next.items = items;
        return next;
      });
    }
  };

  // ---------- render ----------
  const items = Array.isArray(data) ? data : [];

  return (
    <div className="d-flex flex-column pt-48">
      <h4 className="fw-500 fs-18 lh-20 text-eerie-black">About</h4>

      <div className="e-glass-card rounded-16 p-3 d-flex flex-column row-gap-3">
        {items.map((item, idx) => {
          const itemErr = getItemErrors(idx);
          return (
            <div key={item.id} className="d-flex flex-column row-gap-3">
              <div className="d-flex flex-column row-gap-2 pb-2">
                <div className="d-flex align-items-center justify-content-between">
                  <p className="mb-0 fw-400 fs-14">
                    Upload image <span className="text-cg-red">*</span>
                  </p>
                  {idx !== 0 && (
                    <PrimaryButton
                      icon="close"
                      size="12"
                      ClassName="bg-transparent text-cg-red e-border border-pure-red rounded-circle px-6 py-0"
                      onPress={() => removeAboutItem(idx)}
                    />
                  )}
                </div>

                <CustomFileUpload
                  file={item.file || item.fileUrl}
                  type={2}
                  fileSize={3}
                  handleClear={() => handleFileClear(idx)}
                  error={itemErr?.fileError}
                  handleChange={(file) => handleFileSelect(idx, file)}
                  onUploadComplete={(url) => handleFileUploadComplete(idx, url)}
                />
              </div>

              <Input
                id={`about_${item.id}`}
                type="text"
                label="About"
                placeholder="Enter About description"
                require={true}
                value={item.text || ""}
                className="w-100 px-3 py-10"
                error={itemErr?.textError}
                handleChange={(v) => handleTextChange(idx, v)}
              />
            </div>
          );
        })}
        <div className="pt-2">
          <PrimaryButton
            ClassName="bg-transparent lh-22 fs-14 fw-500 text-facebook-blue p-0 rounded-0 e-border-bottom-dashed-blue"
            name="Add new option"
            onPress={addAboutItem}
            disabled={items.length >= maxItems}
          />
        </div>
      </div>
    </div>
  );
}

AboutSections.propTypes = {
  data: PropTypes.array,
  onChange: PropTypes.func,
  maxItems: PropTypes.number,
  errorState: PropTypes.object,
  setErrorState: PropTypes.func,
};
